import React, { useCallback, useEffect, useState } from "react"

import { banWordApi } from "@shared/apis"
import { FontSizeRatio } from "@shared/components/layerPopups/FontSetting"
import { GROUP as GLOBAL_GROUP } from "@shared/config"
import {
  Role,
  useAccount,
  useChatInfo,
  useLayerPopupContext,
  useSchedule,
} from "@shared/contexts"
import {
  db,
  easterEgg,
  FirebaseRef,
  getChatSound,
  getRef,
  maskName,
  rand,
  trimAll,
} from "@shared/modules"
import { messageService } from "@shared/services"

import { snakeCasify } from "casify"

const { toMessage, getManagerMessages, getSystemMessage } = messageService

interface Props {
  limitCount?: number
  firebaseRef?: FirebaseRef
}

export default function useMessages({
  limitCount = 100,
  firebaseRef = "messages",
}: Props) {
  const { account } = useAccount()
  const { schedule } = useSchedule()
  const {
    open,
    alert: { handleAlert },
  } = useLayerPopupContext()
  const { chatMenu } = useChatInfo()

  const [messages, setMessages] = useState<Message[]>([])
  const [managerMessages, setManagermMessages] = useState<Message[]>([])
  const [sentViewer, setSentViewer] = useState<string[]>([])

  const scheduleKey = schedule?.key

  const [lastStreamerMessage, setLastStreamerMessage] = useState<
    Message | undefined
  >(undefined)

  const [keyword, setKeyword] = useState<string | undefined>()
  const [positions, setPositions] = useState<string[]>([])
  const [isMentionAlert, setIsMentionAlert] = useState<boolean>(false)

  const [banWords, setBanWords] = useState<RegExp>(/./)

  const { role, nickname } = account?.viewer!

  const isNotManagerGroupRole = !GROUP.MANAGER.includes(role)
  const isManagerGroupRole = !isNotManagerGroupRole
  const isPaidUser = !GLOBAL_GROUP.UNPAID.includes(role)
  const isPartnerMessage = firebaseRef === "partner_messages"
  const fbUniqueId = account.currentUser?.uid
  const isLoginUser = GLOBAL_GROUP.LOGIN_USER.includes(role)
  const isPaidSchedule = schedule?.paymentInformation === "PAID"
  const isAllSchdule = schedule?.paymentInformation === "ALL"

  const handleIsMentionAlert = useCallback((isEnable: boolean) => {
    if (isEnable) {
      if (timer) {
        clearTimeout(timer)
      }

      timer = setTimeout(() => {
        setIsMentionAlert(false)
        timer = undefined
      }, ALERT_DEACTIVE_SECOND)
    }

    setIsMentionAlert(isEnable)
  }, [])

  useEffect(() => {
    const id = schedule?.id
    if (!id) return

    banWordApi.get(id).then(({ data }) => {
      const { content: banWords } = data
      setBanWords(new RegExp(banWords.map((banWord) => banWord.word).join("|")))
    })
  }, [schedule])

  useEffect(() => {
    if (!scheduleKey || !fbUniqueId || !nickname) return
    ;(async () => {
      const loadManagerMessages = await getManagerMessages(scheduleKey)
      setLastStreamerMessage(
        loadManagerMessages[loadManagerMessages.length - 1],
      )

      if (isNotManagerGroupRole && isPaidSchedule) {
        // 스트리머, 매니저가 아닐 때에는 시스템 메세지도 같이 노출되도록 하기 위해서....
        const systemMessage = await getSystemMessage(scheduleKey)
        const managerAndSystemMessage = [
          ...loadManagerMessages,
          ...systemMessage,
        ].sort(
          (prev: Message, next: Message) => prev.timestamp! - next.timestamp!,
        )
        const announceMessage = [NOTICE_MESSAGE.Guide]

        if (isLoginUser) {
          const randomWelcomeMessage =
            WELCOME_MESSAGE[rand(0, WELCOME_MESSAGE.length - 1)]
          setManagermMessages([
            ...managerAndSystemMessage,
            ...announceMessage,
            randomWelcomeMessage,
          ])
        } else {
          setManagermMessages([...managerAndSystemMessage, ...announceMessage])
        }
      } else {
        setManagermMessages(loadManagerMessages)
      }
    })()

    const append = (snapshot: firebase.database.DataSnapshot) => {
      const message: Message = toMessage(snapshot.val())

      message.key = snapshot.key!.toString()
      message.beautifulBody = message.body

      if (getChatSound() === "ON" && isNotManagerGroupRole) {
        audio.play()
      }

      if (
        !schedule?.isChatEnabled &&
        isNotManagerGroupRole &&
        !GROUP.MANAGER.includes(message.role)
      ) {
        return
      }

      setMessages((prevMessages) => {
        if (prevMessages.some((msg) => msg.key === message.key))
          return prevMessages
        return [...prevMessages, message]
      })

      if (isPartnerMessage) return

      if (!message.isSystem) {
        setSentViewer((prevSentViewer) => {
          return Array.from(
            new Set([...prevSentViewer, trimAll(message.nickname)]),
          )
        })
      } else {
        if (isNotManagerGroupRole && isPaidSchedule) {
          setManagermMessages((prevMessages) => {
            if (prevMessages.some((msg) => msg.key === message.key))
              return prevMessages
            return [...prevMessages, message]
          })
        }
      }

      if (GROUP.MANAGER.includes(message.role)) {
        if (message.body.match(new RegExp(`:@${trimAll(nickname)}:`))) {
          handleIsMentionAlert(true)
        }

        setManagermMessages((prevMessages) => {
          if (prevMessages.some((msg) => msg.key === message.key))
            return prevMessages
          return [...prevMessages, message]
        })
      }

      if (GLOBAL_GROUP.STREAMER.includes(message.role)) {
        setLastStreamerMessage(message)
      }
    }

    const blindMessagesRef = getRef("blind_messages").child(scheduleKey)

    !isPartnerMessage &&
      blindMessagesRef.on(
        "value",
        (snapshot: firebase.database.DataSnapshot) => {
          const messageKey = snapshot.val()

          if (!messageKey) return

          setMessages((prevMessages: Message[]) => {
            return prevMessages.map((message: Message) =>
              messageKey === message.key
                ? { ...message, isBlind: true }
                : message,
            )
          })
        },
      )

    const messageQuery: firebase.database.Query = db()
      .ref(firebaseRef)
      .child(scheduleKey)
      .orderByChild("timestamp")

    messageQuery
      .limitToLast(limitCount)
      .once("value", (snapshot: firebase.database.DataSnapshot) => {
        const messageObject = snapshot.val() || {}

        const messages = Object.keys(messageObject)
          .map((key) => {
            let message: Message = toMessage(messageObject[key])
            message.key = key
            message.beautifulBody = message.body

            return message
          })
          .filter((message: Message) => {
            if (isManagerGroupRole) {
              return true
            } else {
              if (schedule?.isChatEnabled) {
                return true
              } else if (isNotManagerGroupRole && !isPaidSchedule) {
                return GROUP.MANAGER.includes(message.role) || message.isSystem
              } else {
                return true
              }
            }
          })

        let announceMessage = isAllSchdule
          ? [NOTICE_MESSAGE.Guide, NOTICE_MESSAGE.FreeWarning]
          : [NOTICE_MESSAGE.Guide]
        if (isNotManagerGroupRole && !isPaidSchedule) {
          let welcomeMessage: Message
          if (isLoginUser) {
            welcomeMessage =
              WELCOME_MESSAGE[rand(0, WELCOME_MESSAGE.length - 1)]
          } else {
            welcomeMessage = AnonymousWelcomeMessage
          }
          announceMessage = [...announceMessage, welcomeMessage]
        }

        limitCount < 1000
          ? setMessages([
              ...messages.filter((message: Message) =>
                GROUP.USER.includes(message.role),
              ),
              ...announceMessage,
            ])
          : setMessages(
              messages.filter((message: Message) =>
                GROUP.USER.includes(message.role),
              ),
            )
      })

    const subscribemessageRef = messageQuery.startAt(Date.now())

    subscribemessageRef.on("child_added", append)

    return () => {
      subscribemessageRef.off("child_added")
      blindMessagesRef.off("value")
    }
  }, [
    scheduleKey,
    limitCount,
    isNotManagerGroupRole,
    isPaidUser,
    firebaseRef,
    isPartnerMessage,
    fbUniqueId,
    nickname,
    handleIsMentionAlert,
  ])

  useEffect(() => {
    if (!keyword) return
    if (!(chatMenu === "USER" || chatMenu === "MANAGER")) return

    const regex = new RegExp("(" + keyword + ")", "g")
    const highlighting = (prevMessages: Message[]) => {
      const searchedMessageKey: string[] = []
      const newMessages = prevMessages.map((message: Message) => {
        if ((message.key as string).includes("message")) {
          return message
        }

        var messageBody = message.body.toString()
        message.beautifulBody = messageBody
          .split(regex)
          .map((text: string, i: number) => {
            if (text === keyword) {
              searchedMessageKey.unshift(message.key!.toString())
              return <strong key={`${message.key}-${i}`}>{text}</strong>
            }
            return text
          })
        return message
      })

      setPositions(searchedMessageKey)

      return newMessages
    }
    if (chatMenu === "USER") {
      setMessages((prevMessages: Message[]) => highlighting(prevMessages))
    } else if (chatMenu === "MANAGER") {
      setManagermMessages((prevMessages: Message[]) =>
        highlighting(prevMessages),
      )
    }
  }, [keyword, chatMenu])

  const send = useCallback(
    (message: Message): void => {
      if (!schedule || !account.viewer) return

      if (schedule.isActivateBlockChat || account.viewer!.isBlockedChat) {
        alert("채팅금지되어 있습니다.")
        return
      }

      if (message.body.match(banWords)) {
        handleAlert({
          contents: "제한된 키워드가 입력 채팅에 포함되어있습니다.",
        })
        open("ALERT")
        return
      }

      if (easterEgg(message.body)) return

      message.role = account.viewer.role
      message.nickname = account.viewer.nickname

      const fbMessage = {
        ...message,
        userKey: `${account.viewer.username}-${account.viewer.userId}`,
        timestamp: db.ServerValue.TIMESTAMP,
      }

      db()
        .ref(firebaseRef)
        .child(schedule.key)
        .push(snakeCasify(fbMessage), (error: Error | null) => {
          if (error) {
            handleAlert({
              contents:
                "채팅서버와 연결이 끊어졌습니다.\n새로고침 후 다시 시도 부탁드립니다.",
            })
            open("ALERT")
          }
        })
    },
    [schedule, account.viewer, banWords, firebaseRef, open, handleAlert],
  )

  const resetSearch = useCallback((): void => {
    setPositions([])
    setKeyword("")
    setMessages((prevMessages) =>
      prevMessages.map((message) => {
        message.beautifulBody = message.body
        return message
      }),
    )
  }, [])

  const search = useCallback(
    (keyword: string): void => {
      if (keyword.length <= 0) {
        return resetSearch()
      }

      setKeyword(keyword)
    },
    [resetSearch],
  )

  return {
    messages,
    managerMessages,
    send,
    keyword,
    search,
    positions,
    lastStreamerMessage,
    sentViewer,
    isMentionAlert,
  }
}

const audio: HTMLAudioElement = new Audio(
  require("@shared/assets/sounds/chime.mp3"),
)

const GROUP = {
  USER: [
    "USER",
    "STREAMER",
    "HELPER",
    "MANAGER",
    "FREE_USER",
    "PAID_USER",
    "OTHER_PAID_USER",
    "NOT_MATCHED_USER",
    "SYSTEM",
    "PARTNER",
    "ADMIN",
  ],
  MANAGER: ["STREAMER", "HELPER", "PARTNER", "MANAGER"],
}

let timer: NodeJS.Timeout | undefined
const ALERT_DEACTIVE_SECOND = 3 * 1000

const SYSTEM_KEY = "system-999999999"

const NOTICE_MESSAGE = {
  Guide: {
    key: "guide_message",
    body: "관련법에 따라 유료방송에서 양방향 채팅은 금지되며 무료방송에서만 제한 운영됩니다. 또한 한국경제TV와 파트너는 금융투자업자가 아닌 유사투자자문업자로 개별적인 투자 상담과 자금 운영이 불가하며, 원금 손실이 발생할 수 있고 그 손실은 투자자에게 귀속됩니다.",
    beautifulBody: "",
    nickname: "안내",
    emoji: "",
    role: "SYSTEM",
    isSystem: true,
    userKey: SYSTEM_KEY,
  } as Message,
  FreeWarning: {
    key: "warning_message",
    body: "공개방송에 말씀드리는 종목은 유료회원님이 아니면 계속적인 리딩을 받을 수 없으므로 무료회원님들께서는 참고만 하여 주시기 바랍니다. 매매 시에는 반드시 제시하는 손절라인을 엄수하시기 바랍니다.",
    beautifulBody: "",
    nickname: "주의",
    emoji: "",
    role: "SYSTEM",
    isSystem: true,
    userKey: SYSTEM_KEY,
  } as Message,
}

const linkMessage = `${process.env.REACT_APP_WOWNET_CUSTOMER_CENTER_URL}`
const WELCOME_MESSAGE: Message[] = [
  {
    key: "welcome_message0",
    body: `회원님, 어서 오세요! 오늘도 즐거운 하루 보내세요!\n\n회원님의 소중한 의견과 방송 중 문의 및 불편사항은 언제든 와우넷 고객센터로 전화주시기 바랍니다.\n\n☎ 1599-0700 | 고객게시판 ${linkMessage} 클릭`,
    beautifulBody: "",
    color: "#000",
    size: "medium",
    nickname: "와우넷",
    emoji: "",
    role: "MANAGER",
    isSystem: false,
    isWelcome: true,
    userKey: SYSTEM_KEY,
    timestamp: Date.now(),
  },
  {
    key: "welcome_message1",
    body: `회원님, 환영합니다. 오늘도 알찬 하루 보내세요!\n\n회원님의 소중한 의견과 방송 중 문의 및 불편사항은 언제든 와우넷 고객센터로 전화주시기 바랍니다.\n\n☎ 1599-0700 | 고객게시판 ${linkMessage} 클릭`,
    beautifulBody: "",
    color: "#000",
    size: "medium",
    nickname: "와우넷",
    emoji: "",
    role: "MANAGER",
    isSystem: false,
    isWelcome: true,
    userKey: SYSTEM_KEY,
    timestamp: Date.now(),
  },
  {
    key: "welcome_message2",
    body: `회원님, 환영합니다. 오늘도 행운의 날이 되길 기원합니다.\n\n회원님의 소중한 의견과 방송 중 문의 및 불편사항은 언제든 와우넷 고객센터로 전화주시기 바랍니다.\n\n☎ 1599-0700 | 고객게시판 ${linkMessage} 클릭`,
    beautifulBody: "",
    color: "#000",
    size: "medium",
    nickname: "와우넷",
    emoji: "",
    role: "MANAGER",
    isSystem: false,
    isWelcome: true,
    userKey: SYSTEM_KEY,
    timestamp: Date.now(),
  },
  {
    key: "welcome_message3",
    body: `회원님, 어서 오세요! 오늘도 성공적인 투자를 기원합니다.\n\n회원님의 소중한 의견과 방송 중 문의 및 불편사항은 언제든 와우넷 고객센터로 전화주시기 바랍니다.\n\n☎ 1599-0700 | 고객게시판 ${linkMessage} 클릭`,
    beautifulBody: "",
    color: "#000",
    size: "medium",
    nickname: "와우넷",
    emoji: "",
    role: "MANAGER",
    isSystem: false,
    isWelcome: true,
    userKey: SYSTEM_KEY,
    timestamp: Date.now(),
  },
  {
    key: "welcome_message4",
    body: `회원님, 반갑습니다! 오늘도 즐거운 하루 보내세요!\n\n회원님의 소중한 의견과 방송 중 문의 및 불편사항은 언제든 와우넷 고객센터로 전화주시기 바랍니다.\n\n☎ 1599-0700 | 고객게시판 ${linkMessage} 클릭`,
    beautifulBody: "",
    color: "#000",
    size: "medium",
    nickname: "와우넷",
    emoji: "",
    role: "MANAGER",
    isSystem: false,
    isWelcome: true,
    userKey: SYSTEM_KEY,
    timestamp: Date.now(),
  },
  {
    key: "welcome_message5",
    body: `회원님, 오신 것을 진심으로 환영합니다.\n\n회원님의 소중한 의견과 방송 중 문의 및 불편사항은 언제든 와우넷 고객센터로 전화주시기 바랍니다.\n\n☎ 1599-0700 | 고객게시판 ${linkMessage} 클릭`,
    beautifulBody: "",
    color: "#000",
    size: "medium",
    nickname: "와우넷",
    emoji: "",
    role: "MANAGER",
    isSystem: false,
    isWelcome: true,
    userKey: SYSTEM_KEY,
    timestamp: Date.now(),
  },
  {
    key: "welcome_message6",
    body: `회원님, 어서 오세요! 오늘도 함께 해주셔서 감사합니다.\n\n회원님의 소중한 의견과 방송 중 문의 및 불편사항은 언제든 와우넷 고객센터로 전화주시기 바랍니다.\n\n☎ 1599-0700 | 고객게시판 ${linkMessage} 클릭`,
    beautifulBody: "",
    color: "#000",
    size: "medium",
    nickname: "와우넷",
    emoji: "",
    role: "MANAGER",
    isSystem: false,
    isWelcome: true,
    userKey: SYSTEM_KEY,
    timestamp: new Date().getTime(),
  },
]

const AnonymousWelcomeMessage: Message = {
  key: "welcome_message_anonymous",
  body: `고객님, 지금 로그인 하시면 더 많은 서비스를 받으실 수 있습니다. 또한 고객님의 소중한 의견과 방송 중 문의 및 불편사항은 언제든 와우넷 고객센터로 전화주시기 바랍니다.\n\n☎ 1599-0700 | 고객게시판 ${linkMessage} 클릭`,
  beautifulBody: "",
  color: "#000",
  size: "medium",
  nickname: "",
  emoji: "",
  role: "SYSTEM",
  isSystem: false,
  isWelcome: true,
  userKey: SYSTEM_KEY,
  timestamp: new Date().getTime(),
}

export interface Message {
  key: string | number | undefined
  body: string
  beautifulBody: any
  timestamp?: number
  nickname: string
  role: Role
  emoji: string
  color?: string
  size?: FontSizeRatio
  isBold?: boolean
  isSystem?: boolean
  isWelcome?: boolean
  isBlind?: boolean
  userKey: string
  imageUrl?: string
}
